/* ============================================================
   BlogArticle.js — Page article de blog (route /blog/:slug)
   ============================================================
   - SEO complet : <title>, meta description, OG tags, canonical
   - Schema.org JSON-LD : Article + FAQPage + BreadcrumbList
   - FAQ visible (rich snippets Google)
   - CTA Revyo intégrés
   - Maillage interne via relatedSlugs
   ============================================================ */
import { useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import AuroraNavbar from '../components/AuroraNavbar/AuroraNavbar';
import Footer from '../components/Footer';
import { getPostBySlug, getRelatedPosts, ARTICLE_AUTHOR } from '../data/blogPosts';

const SITE_URL = 'https://www.getrevyo.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/* Helper : injecte ou met à jour un <meta name="..." content="..."> */
function upsertMeta(attr, name, content) {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

/* Helper : injecte ou met à jour un <link rel="..." href="..."> */
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

/* Helper : injecte un <script type="application/ld+json"> avec id pour cleanup */
function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('blog');
  const post = getPostBySlug(slug);
  const relatedPosts = getRelatedPosts(slug);

  /* Scroll en haut au changement d'article */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  /* SEO : titre, meta, OG, canonical, JSON-LD — STRICTEMENT INCHANGÉ */
  useEffect(() => {
    if (!post) return;

    const url = `${SITE_URL}/blog/${post.slug}`;
    const ogImage = post.ogImage || DEFAULT_OG_IMAGE;

    // Title + meta description
    const previousTitle = document.title;
    document.title = post.metaTitle;
    const metaDesc = upsertMeta('name', 'description', post.metaDescription);

    // Open Graph (article)
    const ogTitle = upsertMeta('property', 'og:title', post.metaTitle);
    const ogDesc  = upsertMeta('property', 'og:description', post.metaDescription);
    const ogUrl   = upsertMeta('property', 'og:url', url);
    const ogType  = upsertMeta('property', 'og:type', 'article');
    const ogImg   = upsertMeta('property', 'og:image', ogImage);
    const ogPub   = upsertMeta('property', 'article:published_time', post.publishDate);
    const ogSec   = upsertMeta('property', 'article:section', post.category);

    // Twitter
    const twTitle = upsertMeta('name', 'twitter:title', post.metaTitle);
    const twDesc  = upsertMeta('name', 'twitter:description', post.metaDescription);
    const twImg   = upsertMeta('name', 'twitter:image', ogImage);
    const twCard  = upsertMeta('name', 'twitter:card', 'summary_large_image');

    // Canonical
    const canonical = upsertLink('canonical', url);

    // JSON-LD Article
    upsertJsonLd('ld-article', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDescription,
      image: [ogImage],
      datePublished: post.publishDate,
      dateModified: post.publishDate,
      author: ARTICLE_AUTHOR,
      publisher: {
        '@type': 'Organization',
        name: 'Revyo',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/web-app-manifest-512x512.png` }
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      keywords: post.keyword
    });

    // JSON-LD FAQPage
    if (post.faq && post.faq.length > 0) {
      upsertJsonLd('ld-faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faq.map(({ question, answer }) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer }
        }))
      });
    }

    // JSON-LD Breadcrumb
    upsertJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url }
      ]
    });

    // JSON-LD additionnels propres à l'article (ex. SoftwareApplication sur une
    // money page). Aligné avec scripts/generate-blog.js (champ post.extraJsonLd).
    const extraLdIds = (post.extraJsonLd || []).map((ld, i) => {
      const id = `ld-extra-${i}`;
      upsertJsonLd(id, ld);
      return id;
    });

    // Cleanup au démontage
    return () => {
      document.title = previousTitle;
      // On ne supprime pas les meta : la page suivante les écrasera. Mais on
      // retire les JSON-LD spécifiques à l'article pour éviter qu'ils traînent.
      ['ld-article', 'ld-faq', 'ld-breadcrumb', ...extraLdIds].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
      // No-op pour silencer le warning ESLint sur les variables non utilisées
      void [metaDesc, ogTitle, ogDesc, ogUrl, ogType, ogImg, ogPub, ogSec,
            twTitle, twDesc, twImg, twCard, canonical];
    };
  }, [post, slug]);

  /* 404 si slug inconnu */
  if (!post) {
    return (
      <>
        <AuroraBackground dim />
        <AuroraNavbar activeLink="blog" />
        <style>{blogArticleCss}</style>
        <div className="ba-page-wrap">
          <section className="ba-404">
            <h1 className="ba-h1">Article introuvable</h1>
            <p>{t('article.not_found')}</p>
            <Link to="/blog" className="ba-back-btn">← Retour au blog</Link>
          </section>
        </div>
        <Footer />
      </>
    );
  }

  /* Date FR */
  const dateFr = new Date(post.publishDate).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <>
      <AuroraBackground dim />
      <AuroraNavbar activeLink="blog" />

      <style>{blogArticleCss}</style>
      <div className="ba-page-wrap">

        {/* BREADCRUMB + HERO */}
        <article className="ba-article">
          <div className="ba-container">
            <nav className="ba-breadcrumb" aria-label="Fil d'Ariane">
              <Link to="/">Accueil</Link>
              <span aria-hidden="true"> › </span>
              <Link to="/blog">Blog</Link>
              <span aria-hidden="true"> › </span>
              <span className="ba-current">{post.category}</span>
            </nav>

            <div className="ba-meta-top">
              <span className="ba-tag">{post.category}</span>
              <span className="ba-meta-sep">·</span>
              <span className="ba-meta-text">{dateFr}</span>
              <span className="ba-meta-sep">·</span>
              <span className="ba-meta-text">{post.readTime} de lecture</span>
            </div>

            <h1 className="ba-h1">{post.title}</h1>
            <p className="ba-excerpt">{post.excerpt}</p>
          </div>

          {/* CONTENU PRINCIPAL */}
          <div className="ba-container ba-body">
            <div
              className="ba-content"
              dangerouslySetInnerHTML={{ __html: post.htmlContent }}
            />
          </div>

          {/* FAQ visible */}
          {post.faq && post.faq.length > 0 && (
            <div className="ba-container">
              <section className="ba-faq" aria-labelledby="faq-title">
                <h2 id="faq-title" className="ba-faq-title">{t('article.faq_title')}</h2>
                <div className="ba-faq-list">
                  {post.faq.map((item, idx) => (
                    <details key={idx} className="ba-faq-item">
                      <summary className="ba-faq-q">{item.question}</summary>
                      <div className="ba-faq-a">{item.answer}</div>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          )}

          {/* CTA final */}
          <div className="ba-container">
            <section className="ba-final-cta">
              <h2 className="ba-final-title">{t('article.cta_title')}&nbsp;</h2>
              <p className="ba-final-sub">
                {t('article.cta_text')}
                <strong> 14 jours offerts, sans engagement.</strong>
              </p>
              <div className="ba-final-actions">
                <button className="ba-final-primary" onClick={() => navigate('/register')}>
                  {t('article.cta_button')}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </button>
                <button className="ba-final-ghost" onClick={() => navigate('/score-rps')}>
                  Tester mon RPS Score
                </button>
              </div>
            </section>
          </div>

          {/* Articles liés (maillage interne) */}
          {relatedPosts.length > 0 && (
            <div className="ba-container">
              <section className="ba-related">
                <h2 className="ba-related-title">{t('article.related_title')}</h2>
                <div className="ba-related-grid">
                  {relatedPosts.map((rp) => (
                    <Link key={rp.slug} to={`/blog/${rp.slug}`} className="ba-related-card">
                      <span className="ba-related-cat">{rp.category}</span>
                      <h3 className="ba-related-h3">{rp.title}</h3>
                      <p className="ba-related-excerpt">{rp.excerpt}</p>
                      <span className="ba-related-link">Lire l'article →</span>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          )}
        </article>

      </div>
      <Footer />
    </>
  );
}

const blogArticleCss = `
/* Wrapper contenu — critique : position:relative + z-index pour passer au-dessus de aurora-bg */
.ba-page-wrap {
  position: relative;
  z-index: 1;
  padding-top: 88px;
}

/* === Article container === */
.ba-article { padding: 60px 0 100px; }
.ba-container { max-width: 720px; margin: 0 auto; padding: 0 28px; }
.ba-body { margin-top: 48px; }

/* === Breadcrumb === */
.ba-breadcrumb {
  font-size: 14px;
  color: rgba(245,242,235,.62);
  margin-bottom: 28px;
}
.ba-breadcrumb a {
  color: rgba(245,242,235,.8);
  text-decoration: none;
  transition: color .15s ease;
}
.ba-breadcrumb a:hover { color: #FF8A3D; }
.ba-breadcrumb .ba-current { color: #F5F2EB; font-weight: 500; }

/* === Meta top === */
.ba-meta-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.ba-tag {
  background: rgba(232,80,42,.15);
  color: #FF8A3D;
  padding: 6px 14px;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.ba-meta-sep { color: rgba(245,242,235,.3); }
.ba-meta-text { font-size: 14px; color: rgba(245,242,235,.62); }

/* === Titles === */
.ba-h1 {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  letter-spacing: -.025em;
  line-height: 1.1;
  color: #F5F2EB;
  text-wrap: balance;
  margin: 0 0 24px;
}
.ba-excerpt {
  font-size: 21px;
  color: rgba(245,242,235,.78);
  line-height: 1.55;
  text-wrap: pretty;
  font-weight: 400;
  margin: 0;
}

/* === Article content — confort lecture longue === */
.ba-content {
  font-size: 18px;
  line-height: 1.75;
  color: rgba(245,242,235,.92);
}
.ba-content .ba-lead {
  font-size: 21px;
  line-height: 1.55;
  color: #F5F2EB;
  font-weight: 500;
  margin: 0 0 32px;
  text-wrap: pretty;
}
.ba-content p { margin: 0 0 24px; text-wrap: pretty; }
.ba-content h2 {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -.025em;
  line-height: 1.2;
  color: #F5F2EB;
  margin: 56px 0 20px;
}
.ba-content h3 {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 23px;
  font-weight: 600;
  letter-spacing: -.015em;
  line-height: 1.3;
  color: #F5F2EB;
  margin: 40px 0 14px;
}
.ba-content ul, .ba-content ol {
  margin: 0 0 24px;
  padding-left: 24px;
}
.ba-content li { margin-bottom: 10px; }
.ba-content li::marker { color: #E8502A; }
.ba-content strong { color: #F5F2EB; font-weight: 600; }
.ba-content em { font-style: italic; color: rgba(245,242,235,.75); }
.ba-content a { color: #FF8A3D; text-decoration: underline; text-underline-offset: 3px; }
.ba-content .ba-inline-link {
  color: #FF8A3D;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-weight: 500;
}
.ba-content .ba-inline-link:hover { text-decoration-thickness: 2px; }
.ba-content .ba-table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0 28px;
  font-size: 15px;
}
.ba-content .ba-table th,
.ba-content .ba-table td {
  text-align: left;
  padding: 12px 14px;
  border: 1px solid rgba(245,242,235,.12);
  vertical-align: top;
}
.ba-content .ba-table thead th {
  background: rgba(232,80,42,.10);
  color: #F5F2EB;
  font-weight: 600;
}
.ba-content .ba-table tbody tr:nth-child(even) td { background: rgba(245,242,235,.03); }
@media (max-width: 640px) {
  .ba-content .ba-table { font-size: 13px; }
  .ba-content .ba-table th, .ba-content .ba-table td { padding: 9px 10px; }
}
.ba-content .ba-figure { margin: 8px 0 28px; padding: 0; }
.ba-content .ba-figure svg { display: block; width: 100%; height: auto; }
.ba-content .ba-figure figcaption {
  margin-top: 10px;
  font-size: 13px;
  color: rgba(245,242,235,.55);
  text-align: center;
}
.ba-content blockquote {
  margin: 32px 0;
  padding: 20px 24px;
  border-left: 3px solid #E8502A;
  background: rgba(232,80,42,.05);
  border-radius: 0 12px 12px 0;
  color: rgba(245,242,235,.85);
  font-style: italic;
}
.ba-content code {
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(237,234,227,.08);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: .9em;
  color: #FFC9A8;
  font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
}
.ba-content pre {
  background: rgba(10,9,8,.6);
  border: 1px solid rgba(237,234,227,.08);
  border-radius: 12px;
  padding: 20px 24px;
  overflow-x: auto;
  margin: 0 0 24px;
}
.ba-content pre code { background: transparent; border: none; padding: 0; }

/* === CTA box inline (dans le contenu de l'article) === */
.ba-content .ba-cta-box,
.ba-cta-box {
  background: linear-gradient(160deg, rgba(232,80,42,.1) 0%, rgba(255,255,255,.025) 100%);
  border: 1px solid rgba(232,80,42,.25);
  border-radius: 18px;
  padding: 36px 32px;
  margin: 56px 0;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}
.ba-content .ba-cta-title,
.ba-cta-title {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -.015em;
  line-height: 1.2;
  color: #F5F2EB;
  margin: 0 0 12px;
}
.ba-content .ba-cta-text,
.ba-cta-text {
  font-size: 16px;
  color: rgba(245,242,235,.78);
  line-height: 1.55;
  margin: 0 0 22px;
}
.ba-content .ba-cta-actions,
.ba-cta-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.ba-content .ba-btn-primary,
.ba-btn-primary {
  background: linear-gradient(135deg, #E8502A 0%, #FF6B3D 100%);
  color: #fff;
  padding: 13px 22px;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  box-shadow: 0 12px 24px -10px rgba(232,80,42,.5);
  transition: transform .15s ease, box-shadow .15s ease;
}
.ba-content .ba-btn-primary:hover,
.ba-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 32px -10px rgba(232,80,42,.7);
}
.ba-content .ba-btn-secondary,
.ba-btn-secondary {
  background: rgba(255,255,255,.04);
  color: #F5F2EB;
  padding: 13px 22px;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  border: 1px solid rgba(237,234,227,.14);
}
.ba-content .ba-btn-secondary:hover,
.ba-btn-secondary:hover {
  background: rgba(255,255,255,.08);
  border-color: rgba(232,80,42,.4);
}

/* === FAQ === */
.ba-faq {
  margin: 80px 0 0;
  padding-top: 60px;
  border-top: 1px solid rgba(237,234,227,.08);
}
.ba-faq-title {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -.025em;
  color: #F5F2EB;
  margin: 0 0 28px;
}
.ba-faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ba-faq-item {
  background: rgba(255,255,255,.025);
  border: 1px solid rgba(237,234,227,.08);
  border-radius: 14px;
  padding: 20px 24px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: border-color .25s ease;
}
.ba-faq-item[open] { border-color: rgba(232,80,42,.4); }
.ba-faq-q {
  font-size: 17px;
  font-weight: 600;
  color: #F5F2EB;
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.ba-faq-q::-webkit-details-marker { display: none; }
.ba-faq-q::after {
  content: '+';
  font-size: 24px;
  font-weight: 300;
  color: #E8502A;
  transition: transform .25s ease;
}
.ba-faq-item[open] .ba-faq-q::after {
  content: '+';
  transform: rotate(45deg);
}
.ba-faq-a {
  font-size: 16px;
  line-height: 1.65;
  color: rgba(245,242,235,.78);
  margin-top: 14px;
  text-wrap: pretty;
}

/* === Final CTA — gradient orange dense pour l'impact === */
.ba-final-cta {
  background: linear-gradient(135deg, #E8502A 0%, #C73E1D 100%);
  color: #fff;
  border-radius: 24px;
  padding: 56px 44px;
  margin: 80px 0 0;
  text-align: center;
  box-shadow: 0 30px 80px -20px rgba(232,80,42,.4);
  position: relative;
  overflow: hidden;
}
.ba-final-cta::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 0%, rgba(255,255,255,.18) 0%, transparent 60%);
  pointer-events: none;
}
.ba-final-title {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: clamp(26px, 4vw, 34px);
  font-weight: 700;
  letter-spacing: -.025em;
  line-height: 1.15;
  margin: 0 0 16px;
  color: #fff;
  text-wrap: balance;
  position: relative;
}
.ba-final-sub {
  font-size: 17px;
  line-height: 1.55;
  color: rgba(255,255,255,0.92);
  margin: 0 auto 28px;
  max-width: 520px;
  text-wrap: pretty;
  position: relative;
}
.ba-final-sub strong { color: #fff; font-weight: 600; }
.ba-final-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  position: relative;
}
.ba-final-primary {
  background: #fff;
  color: #E8502A;
  padding: 14px 26px;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 12px 24px -8px rgba(0,0,0,.2);
  transition: transform .15s ease, box-shadow .15s ease;
}
.ba-final-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 32px -8px rgba(0,0,0,.3);
}
.ba-final-ghost {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 14px 24px;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background .15s ease;
}
.ba-final-ghost:hover { background: rgba(255,255,255,0.25); }

/* === Related === */
.ba-related {
  margin: 80px 0 0;
  padding-top: 60px;
  border-top: 1px solid rgba(237,234,227,.08);
}
.ba-related-title {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -.02em;
  color: #F5F2EB;
  margin: 0 0 28px;
}
.ba-related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}
.ba-related-card {
  background: rgba(255,255,255,.025);
  border: 1px solid rgba(237,234,227,.08);
  border-radius: 18px;
  padding: 24px;
  text-decoration: none;
  color: #F5F2EB;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: transform .25s, border-color .25s, box-shadow .25s;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ba-related-card:hover {
  transform: translateY(-3px);
  border-color: rgba(232,80,42,.4);
  box-shadow: 0 24px 48px -20px rgba(232,80,42,.25);
}
.ba-related-cat {
  font-size: 12px;
  font-weight: 600;
  color: #FF8A3D;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.ba-related-h3 {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -.01em;
  line-height: 1.3;
  color: #F5F2EB;
  margin: 0;
}
.ba-related-excerpt {
  font-size: 14px;
  color: rgba(245,242,235,.7);
  line-height: 1.5;
  margin: 0;
}
.ba-related-link {
  font-size: 14px;
  font-weight: 600;
  color: #E8502A;
  margin-top: auto;
}

/* === 404 === */
.ba-404 {
  padding: 140px 28px 100px;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  min-height: 60vh;
}
.ba-404 .ba-h1 { color: #F5F2EB; }
.ba-404 p {
  margin: 16px 0 32px;
  color: rgba(245,242,235,.7);
  font-size: 17px;
}
.ba-back-btn {
  display: inline-block;
  background: linear-gradient(135deg, #E8502A 0%, #FF6B3D 100%);
  color: white;
  padding: 13px 24px;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 12px 30px -8px rgba(232,80,42,.55);
}
.ba-back-btn:hover { transform: translateY(-1px); }

/* === Mobile === */
@media (max-width: 900px) {
  .ba-article { padding: 30px 0 70px; }
  .ba-container { padding: 0 20px; }
  .ba-content { font-size: 17px; }
  .ba-content .ba-lead { font-size: 19px; }
  .ba-content h2 { font-size: 26px; margin: 44px 0 16px; }
  .ba-content h3 { font-size: 20px; margin: 32px 0 12px; }
  .ba-cta-box, .ba-content .ba-cta-box { padding: 28px 22px; }
  .ba-cta-title, .ba-content .ba-cta-title { font-size: 21px; }
  .ba-cta-actions, .ba-content .ba-cta-actions { flex-direction: column; }
  .ba-faq-title, .ba-related-title { font-size: 24px; }
  .ba-final-cta { padding: 40px 24px; border-radius: 20px; }
  .ba-final-actions { flex-direction: column; }
  .ba-final-primary, .ba-final-ghost { width: 100%; justify-content: center; }
}
`;
